<script setup lang="ts" generic="T = any, TKey = string | number">
import type { VTreeview } from 'vuetify/components';

type PropsType<T> = {
    items?: T[];
    itemTitle?: string;
    itemValue?: string;
    itemChildren?: string;
    selectStrategy?: VTreeview['$props']['selectStrategy'];
    returnObject?: boolean;
    selectable?: boolean;
    expandIcon?: string;
    collapseIcon?: string;
    density?: VTreeview['$props']['density'];
    variant?: VTreeview['$props']['variant'];
    color?: string;
    loading?: boolean;
    isLabelTranslated?: boolean;
};

withDefaults(defineProps<PropsType<T>>(), {
    itemTitle: 'title',
    itemValue: 'value',
    itemChildren: 'children',
    selectStrategy: 'leaf',
    returnObject: false,
    selectable: true,
    expandIcon: '$expand',
    collapseIcon: '$collapse',
    density: 'default',
    variant: 'text',
    loading: false,
    isLabelTranslated: true,
});

const selected = defineModel<T[] | TKey[]>('selected', { required: true, default: () => [] });
const opened = defineModel<T[]>('opened', { required: true, default: () => [] });
defineOptions({ inheritAttrs: false });

defineSlots<{
    prepend(props: { item: T }): void;
    append(props: { item: T }): void;
    title(props: { item: T; title?: string | number | boolean }): void;
    item(props: { item: T }): void;
}>();
</script>

<template>
    <VTreeview
        v-model:selected="selected"
        v-model:opened="opened"
        :items="items"
        :item-title="itemTitle"
        :item-value="itemValue"
        :item-children="itemChildren"
        :select-strategy="selectStrategy"
        :return-object="returnObject"
        :selectable="selectable"
        :expand-icon="expandIcon"
        :collapse-icon="collapseIcon"
        :density="density"
        :variant="variant"
        :color="color"
        :loading="loading"
        :class="[$attrs.class]"
        v-bind="$attrs"
    >
        <template
            v-if="$slots.prepend"
            #prepend="slotProps"
        >
            <slot
                name="prepend"
                v-bind="slotProps"
            />
        </template>

        <template
            v-if="$slots.append"
            #append="slotProps"
        >
            <slot
                name="append"
                v-bind="slotProps"
            />
        </template>

        <template
            v-if="$slots.title"
            #title="slotProps"
        >
            <slot
                name="title"
                v-bind="slotProps"
            />
        </template>

        <template
            v-if="$slots.item"
            #item="slotProps"
        >
            <slot
                name="item"
                v-bind="{ item: slotProps.props.item }"
            />
        </template>
    </VTreeview>
</template>
